﻿import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import axios from "@/features/axiosHelper";

import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  FormHelperText,
  IconButton,
  DialogContent,
  Dialog,
  DialogTitle,
  FormControlLabel,
  Autocomplete,
  Chip,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import ShepCheckbox from "@/components/GenericComponents/ShepCheckbox";
import {
  NotificationTypeEnum,
  NotificationTypeLabels,
  SeverityLabels,
  SeverityEnum,
  FrequencyEnum,
} from "@/utils/utils";
import ShepMultiCombobox from "@/components/GenericComponents/ShepMultiCombobox";
import ShepButton from "@/components/GenericComponents/ShepButton";
import ShepFormField from "@/components/GenericComponents/ShepFormField";
import { createAlert } from "@/features/homepage/alertSliceList";
import {
  DesktopDateTimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "./AddConfigurationPopup.css";

dayjs.extend(utc);
dayjs.extend(timezone);

const notificationTypes = [
  {
    key: NotificationTypeEnum.Workflow,
    label: NotificationTypeLabels[NotificationTypeEnum.Workflow],
    severities: [
      {
        key: SeverityEnum.Error,
        label: SeverityLabels[SeverityEnum.Error],
      },
      {
        key: SeverityEnum.CompleteWithWarning,
        label: SeverityLabels[SeverityEnum.CompleteWithWarning],
      },
      {
        key: SeverityEnum.Complete,
        label: SeverityLabels[SeverityEnum.Complete],
      },
    ],
  },
  {
    key: NotificationTypeEnum.Endpoint,
    label: NotificationTypeLabels[NotificationTypeEnum.Endpoint],
    severities: [
      {
        key: SeverityEnum.Error,
        label: SeverityLabels[SeverityEnum.Error],
      },
    ],
  },
  {
    key: NotificationTypeEnum.Email,
    label: NotificationTypeLabels[NotificationTypeEnum.Email],
    severities: [
      {
        key: SeverityEnum.Error,
        label: SeverityLabels[SeverityEnum.Error],
      },
      {
        key: SeverityEnum.AwaitingConfirmation,
        label: SeverityLabels[SeverityEnum.AwaitingConfirmation],
      },
    ],
  },
  {
    key: NotificationTypeEnum.FileManager,
    label: NotificationTypeLabels[NotificationTypeEnum.FileManager],
    severities: [
      {
        key: SeverityEnum.Error,
        label: SeverityLabels[SeverityEnum.Error],
      },
    ],
  },
  {
    key: NotificationTypeEnum.API,
    label: NotificationTypeLabels[NotificationTypeEnum.API],
    severities: [
      {
        key: SeverityEnum.Error,
        label: SeverityLabels[SeverityEnum.Error],
      },
    ],
  },
];

const frequencies = [
  {
    key: FrequencyEnum.Immediate,
    label: "Immediate",
  },
  {
    key: FrequencyEnum.Hourly,
    label: "Hourly",
  },
  {
    key: FrequencyEnum.Daily,
    label: "Daily",
  },
  {
    key: FrequencyEnum.Weekly,
    label: "Weekly",
  },
  {
    key: FrequencyEnum.Monthly,
    label: "Monthly",
  },
];

const AddConfigurationPopup = ({
  selectedConfiguration,
  onClose,
  configurationId,
  open,
  configurations,
}) => {
  const dispatch = useDispatch();

  // State to fetch
  const [connectionPairs, setConnectionPairs] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [operations, setOperations] = useState([]);

  // Form state
  const [configurationName, setConfigurationName] = useState("");
  const [selectedNotificationType, setSelectedNotificationType] = useState("");
  const [selectedSeverities, setSelectedSeverities] = useState([]);
  const [selectedConnectionPairs, setSelectedConnectionPairs] = useState([]);
  const [selectedEndpoints, setSelectedEndpoints] = useState([]);
  const [selectedOperations, setSelectedOperations] = useState([]);
  const [selectedFrequency, setSelectedFrequency] = useState(
    FrequencyEnum.Immediate,
  );
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [inAppEnabled, setInAppEnabled] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [emailList, setEmailList] = useState([]);

  const [severityList, setSeverityList] = useState([]);

  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const [emailInputValue, setEmailInputValue] = useState("");
  const [emailInputError, setEmailInputError] = useState(false);

  const getConnectionPairs = () => {
    axios
      .get("/connectionpair")
      .then((response) => {
        setConnectionPairs(response.data ?? []);
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
      });
  };

  const getEndpoints = () => {
    axios
      .get("/endpoint")
      .then((response) => {
        setEndpoints(response.data ?? []);
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
      });
  };

  const getOperations = () => {
    axios
      .get("/connections/operations")
      .then((response) => {
        setOperations(response.data ?? []);
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
      });
  };

  useEffect(() => {
    getConnectionPairs();
    getEndpoints();
    getOperations();
  }, []);

  useEffect(() => {
    prefillFormValues();
  }, [
    selectedConfiguration.NotificationConfigurationId ?? null,
    connectionPairs,
    endpoints,
    operations,
  ]);

  const prefillFormValues = () => {
    const originalNotificationType = notificationTypes.find(
      (type) => type.key === selectedConfiguration?.NotificationType,
    );
    const originalSeverities = originalNotificationType?.severities.filter(
      (severity) => selectedConfiguration?.Severities?.includes(severity.key),
    );
    const originalConnectionPairs = connectionPairs.filter((connectionPair) =>
      selectedConfiguration?.ConnectionPairIds?.includes(
        Number(connectionPair.id),
      ),
    );
    const originalEndpoints = endpoints.filter((endpoint) => {
      return selectedConfiguration?.EndpointIds?.includes(Number(endpoint.id));
    });
    const originalOperations = operations.filter((operation) =>
      selectedConfiguration?.OperationIds?.includes(
        Number(operation.OperationId),
      ),
    );
    setConfigurationName(selectedConfiguration.Name ?? "");
    setSelectedNotificationType(selectedConfiguration?.NotificationType ?? "");
    setSelectedSeverities(originalSeverities ?? []);
    setSelectedConnectionPairs(originalConnectionPairs ?? []);
    setSelectedEndpoints(originalEndpoints ?? []);
    setSelectedOperations(originalOperations ?? []);
    setSelectedFrequency(
      selectedConfiguration.Frequency ?? FrequencyEnum.Immediate,
    );
    setEmailEnabled(selectedConfiguration.EmailEnabled ?? false);
    setInAppEnabled(selectedConfiguration.InAppEnabled ?? false);
    setStartTime(
      selectedConfiguration.StartTimeUTC
        ? dayjs.utc(selectedConfiguration.StartTimeUTC)
        : null,
    );
    setEmailList(selectedConfiguration?.EmailList ?? []);
    setEmailInputError(false);
    setEmailInputValue("");

    setSeverityList(originalNotificationType?.severities ?? []);
  };

  const handleNotificationTypeChange = (e) => {
    if (e.target.value !== undefined) {
      setSelectedNotificationType(e.target.value);
      setSelectedSeverities([]);
      setSelectedConnectionPairs([]);
      setSelectedEndpoints([]);
      setSelectedOperations([]);

      setSeverityList(
        notificationTypes.find((item) => item.key === e.target.value)
          ?.severities ?? [],
      );
    }
  };

  const validateInput = () => {
    if (
      configurationName.length === 0 ||
      selectedNotificationType === "" ||
      selectedFrequency === "" ||
      !isNameUnique()
    ) {
      return false;
    } else if (!emailEnabled && !inAppEnabled) {
      return false;
    } else if (emailEnabled && emailList.length === 0) {
      return false;
    } else if (!emailEnabled && selectedFrequency !== FrequencyEnum.Immediate) {
      return false;
    }
    //If a notification doesn't have a status selected the configuration will simply never fire, so prevent user from doing so. Same with workflow notifications that don't have a connection pair or operation or endpoint notifications and selected endpoints
    else if (selectedSeverities.length === 0) return false;
    else if (
      selectedNotificationType === NotificationTypeEnum.Workflow &&
      (selectedConnectionPairs.length === 0 || selectedOperations.length === 0)
    )
      return false;
    else if (
      selectedNotificationType === NotificationTypeEnum.Endpoint &&
      selectedEndpoints.length === 0
    )
      return false;
    else return true;
  };

  // Create alerts for save status
  const onSavedSuccess = (message) => {
    prefillFormValues();
    dispatch(
      createAlert({
        status: "Success",
        alertType: "result",
        title: "Successfully saved",
        message: message,
        buttonLabel: "Close",
        onClose: onClose,
      }),
    );
  };

  const onSavedError = (message) => {
    dispatch(
      createAlert({
        status: "Error",
        alertType: "result",
        title: "Error Saving",
        message: message,
        buttonLabel: "Ok",
      }),
    );
  };

  const postConfigurationData = () => {
    if (!validateInput()) {
      dispatch(
        createAlert({
          status: "Error",
          alertType: "result",
          title: "Error Saving",
          message: "The configuration was invalid",
          buttonLabel: "Ok",
        }),
      );
    } else {
      if (!selectedConfiguration?.NotificationConfigurationId) {
        axios
          .post("/notifications/add-configuration", {
            Name: configurationName,
            NotificationType: selectedNotificationType,
            EmailEnabled: emailEnabled,
            InAppEnabled: inAppEnabled,
            Frequency: selectedFrequency,
            StartTimeUTC: startTime,
            Severities: selectedSeverities.map((severity) => severity.key),
            ConnectionPairIds: selectedConnectionPairs.map(
              (connectionPair) => connectionPair.id,
            ),
            EndpointIds: selectedEndpoints.map((endpoint) => endpoint.id),
            OperationIds: selectedOperations.map(
              (operation) => operation.OperationId,
            ),
            EmailList: emailList,
          })
          .then((response) => {
            if (response.data) {
              onSavedSuccess(
                "The configuration was created / edited successfully!",
              );

              // heap.io
              window.heap.track("Configurations - Create");
            } else {
              onSavedError("There was an error creating the configuration");
            }
          })
          .catch((error) => {
            console.error("Error", error);
            let errorMessage = error.message;
            onSavedError(errorMessage);
          });
      }
      // editing configuration
      else {
        axios
          .put(
            `/notifications/edit-configuration/${selectedConfiguration.NotificationConfigurationId}`,
            {
              NotificationConfigurationId:
                selectedConfiguration.NotificationConfigurationId,
              Name: configurationName,
              NotificationType: selectedNotificationType,
              EmailEnabled: emailEnabled,
              InAppEnabled: inAppEnabled,
              Frequency: selectedFrequency,
              StartTimeUTC: startTime,
              Severities: selectedSeverities.map((severity) => severity.key),
              ConnectionPairIds: selectedConnectionPairs.map(
                (connectionPair) => connectionPair.id,
              ),
              EndpointIds: selectedEndpoints.map((endpoint) => endpoint.id),
              OperationIds: selectedOperations.map(
                (operation) => operation.OperationId,
              ),
              EmailList: emailList,
            },
          )
          .then((response) => {
            if (response.data) {
              onSavedSuccess(
                "The configuration was created / edited successfully!",
              );

              // heap.io
              window.heap.track("Configurations - Create");
            } else {
              onSavedError("There was an error creating the configuration");
            }
          })
          .catch((error) => {
            console.error("Error", error);
            let errorMessage = error.message;
            onSavedError(errorMessage);
          });
      }
    }
  };

  const isNameUnique = () => {
    return !configurations.some(
      (config) =>
        config.Name === configurationName &&
        config.NotificationConfigurationId !==
          selectedConfiguration.NotificationConfigurationId,
    );
  };

  const isEmailValid = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        PaperProps={{
          style: {
            backgroundColor: "transparent",
            boxShadow: "none",
          },
        }}
        sx={{ zIndex: "1500" }}
      >
        <DialogTitle
          style={{
            display: "flex",
            justifyContent: "space-between",
            backgroundColor: "var(--primary-main)",
            color: "var(--default-light)",
            fontSize: "24px",
            borderRadius: "15px 15px 0 0",
          }}
        >
          <div style={{ display: "flex" }}>
            <div style={{ fontWeight: "var(--font-semi-bold)" }}>
              {selectedConfiguration.NotificationConfigurationId
                ? "Edit Configuration"
                : "Add Configuration"}
            </div>
            <div></div>
          </div>
          <IconButton
            className="closeIconButton"
            style={{ color: "var(--default-light)" }}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            borderRadius: "0 0 16px 16px",
            backgroundColor: "#f9f9f9",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              flex: "auto",
              display: "flex",
              width: 1,
              backgroundColor: "var(--disabled)",
              margin: "40px 0px 0px 0px",
            }}
          >
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: 20,
                padding: "40px !important",
                border: "1.5px solid var(--grey-lowlight)",
                marginBottom: "24px !important",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box style={{ display: "flex" }}>
                  <Box sx={{ marginBottom: "20px", display: "flex" }}>
                    <Box
                      style={{
                        display: "inline-flex",
                        flexWrap: "wrap",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          marginBottom: "20px",
                        }}
                      >
                        <Typography
                          variant="subheading2"
                          className="config-section-header"
                        >
                          Configuration Name
                        </Typography>
                        <Box>
                          <ShepFormField
                            className="config-select"
                            value={configurationName}
                            onChange={(event) => {
                              setConfigurationName(event.target.value);
                            }}
                            error={
                              configurationName.length === 0 || !isNameUnique()
                            }
                            helperText={
                              configurationName.length === 0
                                ? "Name cannot be empty"
                                : !isNameUnique()
                                  ? "There already exists a configuration with this name"
                                  : ""
                            }
                            id="outlined-basic"
                            variant="outlined"
                            label="Enter a unique name for this configuration"
                          />
                        </Box>
                      </Box>
                      <Box
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          marginBottom: "20px",
                        }}
                      >
                        <Typography
                          variant="subheading2"
                          className="config-section-header"
                        >
                          Notification Type
                        </Typography>
                        <FormControl
                          error={selectedNotificationType.length === 0}
                        >
                          <InputLabel>Select Notification Type</InputLabel>
                          <Select
                            value={selectedNotificationType}
                            label="Select Notification Types"
                            onChange={handleNotificationTypeChange}
                            MenuProps={{
                              style: { zIndex: 1500 },
                            }}
                            className="config-select"
                            error={selectedNotificationType.length === 0}
                          >
                            {notificationTypes.map((item) => (
                              <MenuItem key={item.key} value={item.key}>
                                {item.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {selectedNotificationType.length === 0 && (
                            <FormHelperText>
                              Please select a notification type
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Box>
                      <ShepMultiCombobox
                        title="Status"
                        options={severityList}
                        selectedOptions={selectedSeverities}
                        onChange={(e, value) => {
                          if (value.find((option) => option.all)) {
                            if (value.length > severityList.length)
                              setSelectedSeverities([]);
                            else setSelectedSeverities(severityList);
                          } else setSelectedSeverities(value);
                        }}
                        getLabel={(option) => {
                          if (Object.hasOwn(option, "name")) return option.name;
                          else return option.label;
                        }}
                        checkEquality={(option, value) => {
                          return option.key === value.key;
                        }}
                        displayLimit={2}
                        error={selectedSeverities.length === 0}
                        helperText={
                          selectedSeverities.length === 0
                            ? "At least one status needs to be selected"
                            : ""
                        }
                      />
                    </Box>
                  </Box>
                  <Box style={{ display: "flex", flexDirection: "column" }}>
                    <Box style={{ display: "flex", flexDirection: "column" }}>
                      <Typography
                        variant="subheading2"
                        className="config-section-header"
                      >
                        Methods
                      </Typography>
                      <FormControl error={!emailEnabled && !inAppEnabled}>
                        <Box
                          sx={{
                            paddingLeft: 2,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <ShepCheckbox
                                edge="start"
                                disabled={false}
                                checked={emailEnabled}
                                tabIndex={-1}
                                disableRipple
                                onChange={() => {
                                  if (emailEnabled) {
                                    setSelectedFrequency(
                                      FrequencyEnum.Immediate,
                                    );
                                  }
                                  setEmailEnabled(!emailEnabled);
                                }}
                                inputProps={{
                                  "aria-labelledby":
                                    "checkbox-list-label-email",
                                }}
                              />
                            }
                            label={"Email"}
                          />
                          <FormControlLabel
                            control={
                              <ShepCheckbox
                                edge="start"
                                disabled={false}
                                checked={inAppEnabled}
                                tabIndex={-1}
                                disableRipple
                                onChange={() => setInAppEnabled(!inAppEnabled)}
                                inputProps={{
                                  "aria-labelledby":
                                    "checkbox-list-label-in-app",
                                }}
                              />
                            }
                            label={"In-App"}
                          />
                        </Box>
                        <FormHelperText sx={{ marginLeft: 0 }}>
                          {!inAppEnabled && !emailEnabled
                            ? "Please select a notification delivery method"
                            : ""}
                        </FormHelperText>
                      </FormControl>
                    </Box>
                    <Box style={{ display: "flex", flexDirection: "column" }}>
                      <Typography
                        variant="subheading2"
                        className="config-section-header"
                      >
                        Frequency
                      </Typography>
                      <FormControl error={selectedFrequency.length === 0}>
                        <InputLabel>Select Frequency</InputLabel>
                        <Select
                          value={selectedFrequency}
                          label="Select Frequencies"
                          onChange={(event) => {
                            setSelectedFrequency(event.target.value);
                          }}
                          MenuProps={{
                            style: { zIndex: 1500 },
                          }}
                          className="config-select"
                          error={selectedFrequency.length === 0}
                          disabled={!emailEnabled}
                        >
                          {frequencies.map((item) => (
                            <MenuItem key={item.key} value={item.key}>
                              {item.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText sx={{ marginLeft: 0 }}>
                          Note frequency only applies to email notifications—all
                          in-app notifications will be sent immediately
                        </FormHelperText>
                      </FormControl>
                      <Box style={{ marginTop: "10px", paddingBottom: "20px" }}>
                        <FormControl>
                          <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                            adapterLocale="en"
                            localeText={{
                              cancelButtonLabel: "Cancel",
                              okButtonLabel: "Apply",
                            }}
                          >
                            <DesktopDateTimePicker
                              calendars={1}
                              closeOnSelect={false}
                              label="Start Time"
                              showDaysOutsideCurrentMonth
                              value={startTime}
                              onChange={setStartTime}
                              open={timePickerOpen}
                              onClose={() => setTimePickerOpen(false)}
                              slotProps={{
                                textField: {
                                  onClick: () => setTimePickerOpen(true),
                                },
                                popper: {
                                  style: { zIndex: 1500 },
                                  modifiers: [{ name: "flip", enabled: false }],
                                  PopperProps: {
                                    className: "custom-popper-style",
                                  },
                                },
                              }}
                              timezone="system"
                              sx={{ width: "400px" }}
                            />
                          </LocalizationProvider>
                          {startTime === null && (
                            <FormHelperText sx={{ marginLeft: 0 }}>
                              You can set when you'd like the configuration to
                              start collecting notifications, or leave it blank
                              to start collecting it now
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Box>
                    </Box>
                    <Box style={{ display: "flex", flexDirection: "column" }}>
                      <Typography
                        variant="subheading2"
                        className="config-section-header"
                      >
                        Email List
                      </Typography>
                      <Autocomplete
                        sx={{
                          "& .MuiInputBase-root": {
                            alignItems: "center",
                            flexWrap: "wrap",
                            padding: "4px 8px",
                            display: "flex",
                            height: "auto",
                          },
                          width: "400px",
                          marginRight: "80px",
                        }}
                        multiple
                        freeSolo
                        disableCloseOnSelect
                        id={`mutli-combobox-email-list`}
                        options={[]}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Email List Input"
                            placeholder="Input emails..."
                            error={emailInputError}
                            helperText={
                              emailInputError
                                ? "Please input a valid email"
                                : "Press enter to add email to list"
                            }
                          />
                        )}
                        renderValue={(value, getItemProps) =>
                          value.map((option, index) => {
                            const { key, ...itemProps } = getItemProps({
                              index,
                            });
                            return (
                              <Chip
                                variant="outlined"
                                label={option}
                                key={key}
                                {...itemProps}
                              />
                            );
                          })
                        }
                        limitTags={1}
                        onChange={(e, value, reason) => {
                          if (
                            !isEmailValid(value[value.length - 1]) &&
                            reason === "createOption"
                          ) {
                            setEmailInputError(true);
                            setEmailInputValue(value[value.length - 1] ?? "");
                          } else setEmailList(value);
                        }}
                        inputValue={emailInputValue}
                        onInputChange={(e, inputValue) => {
                          if (emailInputError) setEmailInputError(false);
                          setEmailInputValue(inputValue);
                        }}
                        value={emailList}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    marginBottom: "20px",
                    display: "flex",
                    flexWrap: "wrap",
                  }}
                >
                  <Box style={{ display: "inline-flex", flexWrap: "wrap" }}>
                    {selectedNotificationType ===
                      NotificationTypeEnum.Workflow && (
                      <ShepMultiCombobox
                        title="Connection Pairs"
                        options={connectionPairs}
                        selectedOptions={selectedConnectionPairs}
                        onChange={(e, value) => {
                          if (value.find((option) => option.all)) {
                            if (value.length > connectionPairs.length)
                              setSelectedSeverities([]);
                            else setSelectedConnectionPairs(connectionPairs);
                          } else setSelectedConnectionPairs(value);
                        }}
                        getLabel={(option) => {
                          if (Object.hasOwn(option, "name")) return option.name;
                          else return option.ConnectionPairName;
                        }}
                        checkEquality={(option, value) => {
                          if (option.all) return value.all === true;
                          return option.id === value.id;
                        }}
                        displayLimit={2}
                        error={selectedConnectionPairs.length === 0}
                        helperText={
                          selectedConnectionPairs.length === 0
                            ? "At least one connection pair needs to be selected"
                            : ""
                        }
                      />
                    )}
                    {selectedNotificationType ===
                      NotificationTypeEnum.Endpoint && (
                      <ShepMultiCombobox
                        title="Endpoints"
                        options={endpoints}
                        selectedOptions={selectedEndpoints}
                        onChange={(e, value) => {
                          if (value.find((option) => option.all)) {
                            if (value.length > endpoints.length)
                              setSelectedEndpoints([]);
                            else setSelectedEndpoints(endpoints);
                          } else setSelectedEndpoints(value);
                        }}
                        getLabel={(option) => {
                          if (Object.hasOwn(option, "name")) return option.name;
                          else return option.EndpointName;
                        }}
                        checkEquality={(option, value) => {
                          if (option.all) return value.all === true;
                          return option.id === value.id;
                        }}
                        displayLimit={2}
                        error={selectedEndpoints.length === 0}
                        helperText={
                          selectedEndpoints.length === 0
                            ? "At least one endpoint needs to be selected"
                            : ""
                        }
                      />
                    )}
                    {selectedNotificationType ===
                      NotificationTypeEnum.Workflow && (
                      <ShepMultiCombobox
                        title="Workflows"
                        options={operations}
                        selectedOptions={selectedOperations}
                        onChange={(e, value) => {
                          if (value.find((option) => option.all)) {
                            if (value.length > operations.length)
                              setSelectedOperations([]);
                            else setSelectedOperations(operations);
                          } else setSelectedOperations(value);
                        }}
                        getLabel={(option) => {
                          if (Object.hasOwn(option, "name")) return option.name;
                          else return option.OperationName;
                        }}
                        checkEquality={(option, value) => {
                          if (option.all) return value.all === true;
                          return option.OperationId === value.OperationId;
                        }}
                        displayLimit={1}
                        error={selectedOperations.length === 0}
                        helperText={
                          selectedOperations.length === 0
                            ? "At least one workflow needs to be selected"
                            : ""
                        }
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            style={{
              margin: "0px 24px",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <ShepButton
                variant="secondary"
                className="second-button"
                onClick={onClose}
              >
                Close
              </ShepButton>
              <ShepButton onClick={postConfigurationData}>Save</ShepButton>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddConfigurationPopup;
